import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SectionOne from "./tab-section/SectionOne";
import SectionTwo from "./tab-section/SectionTwo";
import SectionThree from "./tab-section/SectionThree";
import SectionFour from "./tab-section/SectionFour";
import SectionFive from "./tab-section/SectionFive";
import SectionSix from "./tab-section/SectionSix";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "../Banner";
import Offer from "../Offer";
import PreviewBanner from "../PreviewBanner";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BottomBanner from "../BottomBanner";
import HeroSection from "./tab-section/HeroSection";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import LaptopIcon from "@mui/icons-material/Laptop";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { togglePlatform } from "../../../redux/counterSlice";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-hot-toast";

const drawerWidth = 200;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
  paddingTop: "16px",
  paddingBottom: "16px",
  margin: "0 auto",
}));

const MainContent = styled(Box)(({ theme, open }) => ({
  width: `calc(100% - ${open ? drawerWidth : 0}px)`,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const HomeSectionTabDrawer = memo(({
  open,
  platform,
  handleChange,
  handleMenuClick,
  selectedMenu,
}) => {
  const theme = useTheme();

  return (
    <Drawer
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          top: "48px",
          width: open ? drawerWidth : 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
      variant="persistent"
      anchor="right"
      open={open}
    >
      <DrawerHeader>
        <Box display={"flex"} flexDirection={"column"} gap={2}>
          <Typography variant="h6">Home Sections</Typography>
          <ToggleButtonGroup
            color="primary"
            value={platform || "app"}
            exclusive
            onChange={handleChange}
            sx={{ justifyContent: "center" }}
            aria-label="Platform"
          >
            <ToggleButton value="app">
              <PhoneAndroidIcon />
            </ToggleButton>
            <ToggleButton value="web">
              <LaptopIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DrawerHeader>
      <Divider />
      <List>
        {[
          "Hero",
          "Banner",
          "Offer",
          "Preview Banner",
          "Best Seller",
          "New Arrivals",
          "Flavour",
          "Categories",
          "Sub Categories",
          "Custom",
          "Bottom Banner",
        ].map((text) => (
          <ListItem
            key={text}
            disablePadding
            onClick={() => handleMenuClick(text)}
          >
            <ListItemButton>
              <ChevronRightIcon
                fontSize="small"
                sx={{
                  color: text === selectedMenu ? "red" : "inherit",
                }}
              />
              <ListItemText
                primary={text}
                sx={{
                  pl: 1,
                  color: text === selectedMenu ? "red" : "inherit",
                  "& span": {
                    fontWeight: text === selectedMenu ? 500 : "inherit",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </Drawer>
  );
});

const MenuScreen = memo(({
  selectedMenu,
  regionId,
  data,
  initialLoading,    // ✅ only true on very first load
  refreshBannerData, // ✅ silent refresh — no loader flash
  refreshOfferData,
  loadPreviewBannerData,
  loader,
  setLoader,
}) => {
  switch (selectedMenu) {
    case "Hero":
      return <HeroSection regionId={regionId} />;
    case "Banner":
      return (
        <Banner
          key="banner-section"
          regionId={regionId}
          loadInitialData={refreshBannerData}   // ✅ silent refresh
          formData={data?.bannerResponse}
          routeItems={data?.routeResponse}
          loader={initialLoading}               // ✅ only blinks on first load
          setLoader={setLoader}
        />
      );
    case "Offer":
      return (
        <Offer
          key="offer-section"
          regionId={regionId}
          loadInitialData={refreshOfferData}
          formData={data?.offerResponse}
          headings={data?.offerResponse?.headings}
          routeItems={data?.routeResponse}
          loader={initialLoading}
          setLoader={setLoader}
        />
      );
    case "Preview Banner":
      return (
        <PreviewBanner
          loadInitialData={loadPreviewBannerData}
          regionId={regionId}
          formData={data}
          loader={loader}
          setLoader={setLoader}
        />
      );
    case "Best Seller":
      return <SectionOne regionId={regionId} />;
    case "New Arrivals":
      return <SectionTwo regionId={regionId} />;
    case "Flavour":
      return <SectionThree regionId={regionId} />;
    case "Sub Categories":
      return <SectionFour regionId={regionId} />;
    case "Custom":
      return <SectionFive regionId={regionId} />;
    case "Bottom Banner":
      return <BottomBanner regionId={regionId} />;
    case "Categories":
      return <SectionSix regionId={regionId} />;
    default:
      return null;
  }
});

function HomeSection() {
  const { regionId, city } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const platform = useSelector((state) => state.counter?.platform);
  const [open, setOpen] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState("Hero");
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(false);
  // ✅ Separate state: only true on very first load, never again
  const [initialLoading, setInitialLoading] = useState(true);

  const handleMenuClick = useCallback((menu) => setSelectedMenu(menu), []);
  const handleDrawerOpen = useCallback(() => setOpen(true), []);
  const handleDrawerClose = useCallback(() => setOpen(false), []);
  const handleChange = useCallback((event, value) => {
    dispatch(togglePlatform(value));
  }, [dispatch]);

  // ✅ Full load — shows loader (first time only)
  const loadBannerData = useCallback(() => {
    setInitialLoading(true);
    axios
      .all([
        axios.get("/bannerByRegion/" + regionId),
        axios.get("/getAllRouteItems"),
      ])
      .then(
        axios.spread((bannerResponse, routeResponse) => {
          setData({
            bannerResponse: bannerResponse?.data?.data,
            routeResponse: routeResponse?.data?.data,
          });
          setInitialLoading(false);
        })
      )
      .catch(function (err) {
        toast.error(err?.message);
        setInitialLoading(false);
      });
  }, [regionId]);

  // ✅ Silent refresh — NO loader, just updates data in background
  // This is what ImageUpload calls after toggle/upload — no blink!
  const refreshBannerData = useCallback(() => {
    axios
      .all([
        axios.get("/bannerByRegion/" + regionId),
        axios.get("/getAllRouteItems"),
      ])
      .then(
        axios.spread((bannerResponse, routeResponse) => {
          setData({
            bannerResponse: bannerResponse?.data?.data,
            routeResponse: routeResponse?.data?.data,
          });
        })
      )
      .catch(function (err) {
        toast.error(err?.message);
      });
  }, [regionId]);

  const loadPreviewBannerData = useCallback(() => {
    axios
      .get("/getHomeGeneralBanner/" + regionId)
      .then(function (response) {
        setData(response?.data?.data);
        setLoader(false);
      })
      .catch(function (err) {
        toast.error(err?.message);
      });
  }, [regionId]);

  const loadOfferData = useCallback(() => {
    setInitialLoading(true);
    axios
      .all([
        axios.get("/offerByRegion/" + regionId),
        axios.get("/getAllRouteItems"),
      ])
      .then(
        axios.spread((offerResponse, routeResponse) => {
          setData({
            offerResponse: offerResponse?.data?.data,
            routeResponse: routeResponse?.data?.data,
          });
          setInitialLoading(false);
        }),
      )
      .catch(function (err) {
        toast.error(err?.message);
        setInitialLoading(false);
      });
  }, [regionId]);

  const refreshOfferData = useCallback(() => {
    axios
      .all([
        axios.get("/offerByRegion/" + regionId),
        axios.get("/getAllRouteItems"),
      ])
      .then(
        axios.spread((offerResponse, routeResponse) => {
          setData((prev) => ({
            ...prev,
            offerResponse: offerResponse?.data?.data,
            routeResponse: routeResponse?.data?.data,
          }));
        }),
      )
      .catch(function (err) {
        toast.error(err?.message);
      });
  }, [regionId]);

  useEffect(() => {
    selectedMenu == "Banner" && loadBannerData();
    selectedMenu == "Offer" && loadOfferData();
    selectedMenu == "Preview Banner" && loadPreviewBannerData();
  }, [selectedMenu]);

  return (
    <>
      <MainContent open={open} sx={{ flexGrow: 1, padding: 2 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            flexDirection: "row",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="d-flex ">
            <ArrowBackIcon
              className="text-danger"
              sx={{ marginRight: "1rem", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            <h6 className="text-dark">Home Section / {city}</h6>
          </div>
          <Box
            flexDirection={"row"}
            display={"flex"}
            gap={3}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerOpen}
              sx={[open && { display: "none" }]}
            >
              <MenuIcon />
            </IconButton>
            {!open && (
              <h6 className="text-dark" style={{ margin: 0 }}>
                {selectedMenu}
              </h6>
            )}
          </Box>
        </Box>

        <MenuScreen
          selectedMenu={selectedMenu}
          regionId={regionId}
          data={data}
          initialLoading={initialLoading}
          refreshBannerData={refreshBannerData}
          refreshOfferData={refreshOfferData}
          loadPreviewBannerData={loadPreviewBannerData}
          loader={loader}
          setLoader={setLoader}
        />
      </MainContent>

      <HomeSectionTabDrawer
        open={open}
        platform={platform}
        handleChange={handleChange}
        handleMenuClick={handleMenuClick}
        selectedMenu={selectedMenu}
      />
    </>
  );
}

export default HomeSection;